import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import articlePlaceholder from '../../../assets/articlePlaceholder.png';
import { ModalViews } from '../../../constants';
import { useThemeContext } from '../../../global-state/themeContext';
import { StockCountItem, StockStatusType } from '../../../hooks';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { soldProductsTranslations, yearlyStockTakeTranslations } from '../../../translations';
import { getArticleAssetUrl } from '../../../utils/soldProducts';
import { formatSeason } from '../util';
import { StyledIcon } from '../YearlyStockTakePage.styles';
import {
  StyledActionIcon,
  StyledImage,
  StyledMultipleCardWrapper,
  StyledTypography,
} from './StockDetailsCard.styles';

export type StockDetailsCardProps = {
  stockCountItem: StockCountItem;
  isBulkAction: boolean;
  modalView?: ModalViews;
  status?: StockStatusType;
  footerContent?: JSX.Element;
  isDrawerContent?: boolean;
  hasborder?: boolean;
  removeItem?: () => void;
};

const DetailRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <RowFlexContainer justifyContent="between">
    <Typography color="textGray">{label}</Typography>
    <Typography>{value}</Typography>
  </RowFlexContainer>
);

export const StockDetailsCard: FC<StockDetailsCardProps> = ({
  stockCountItem,
  isBulkAction,
  footerContent,
  modalView,
  isDrawerContent,
  hasborder = true,
  removeItem,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { stockTable: stockTableTranslations } = yearlyStockTakeTranslations;
  const { articleId, sicScope, productName, productId, departmentName, departmentNumber, index, subIndex } =
    stockCountItem;
  const imageUrl = getArticleAssetUrl({
    articleId: `${articleId}`,
    assetType: 'descriptiveStillLife',
    rendition: 'medium',
  });

  const getIcon = () => {
    return modalView === ModalViews.INITIAL
      ? Icons.CLOSE
      : modalView === ModalViews.SUCCESS
      ? Icons.CHECK_MARK_CIRCLE
      : Icons.WARNING;
  };

  return (
    <>
      {!isBulkAction && (
        <ColumnFlexContainer
          hasBorder={hasborder}
          shadow={hasborder}
          borderRadius={[4]}
          backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
          data-testid="stock-details-card"
        >
          <ColumnFlexContainer padding={hasborder ? [4] : [0]}>
            {isDrawerContent && (
              <RowFlexContainer justifyContent="center" margin={[0, 0, 2, 0]}>
                <StyledImage
                  data-testid="article-image"
                  src={imageUrl}
                  size="large"
                  alt={productName}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = articlePlaceholder;
                  }}
                />
              </RowFlexContainer>
            )}
            <RowFlexContainer justifyContent="between" margin={[0, 0, 4]}>
              <ContainerV2 display="flex" flexDirection={isDrawerContent ? 'row' : 'column'} width="100%">
                <ColumnFlexContainer flex={1}>
                  <Typography type="caption">
                    {t(
                      stockTableTranslations.articleNumber.key,
                      stockTableTranslations.articleNumber.defaultValue,
                    )}
                  </Typography>
                  <Typography type="h3">{sicScope}</Typography>
                </ColumnFlexContainer>
                <ColumnFlexContainer
                  padding={[1, 2]}
                  alignSelf="start"
                  alignItems="end"
                  backgroundColor="validationGreen"
                  borderRadius={[1]}
                >
                  <Typography color="white" type="h4">
                    {formatSeason(sicScope)}
                  </Typography>
                  <Typography color="white" type="caption">
                    {t(soldProductsTranslations.season.key, soldProductsTranslations.season.defaultValue)}
                  </Typography>
                </ColumnFlexContainer>
              </ContainerV2>
              {!isDrawerContent && (
                <StyledImage
                  data-testid="article-image"
                  src={imageUrl}
                  size="medium"
                  alt={productName}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = articlePlaceholder;
                  }}
                />
              )}
            </RowFlexContainer>
            <ColumnFlexContainer gap={[2]}>
              <DetailRow
                label={t(stockTableTranslations.productName.key, stockTableTranslations.productName.defaultValue)}
                value={productName}
              />
              <DetailRow
                label={t(stockTableTranslations.productId.key, stockTableTranslations.productId.defaultValue)}
                value={productId}
              />
              <DetailRow
                label={t(
                  stockTableTranslations.departmentName.key,
                  stockTableTranslations.departmentName.defaultValue,
                )}
                value={departmentName}
              />
              <DetailRow
                label={t(
                  stockTableTranslations.departmentId.key,
                  stockTableTranslations.departmentId.defaultValue,
                )}
                value={departmentNumber}
              />
              <RowFlexContainer gap={[2]} flex={1}>
                <RowFlexContainer
                  flex={1}
                  borderRadius={[2]}
                  gap={[3]}
                  backgroundColor={mode === 'dark' ? 'backgroundSelectedHover' : 'backgroundTableHeader'}
                  padding={[3]}
                >
                  <Icon icon={Icons.PRODUCTCATEGORY} size="small" />
                  <ColumnFlexContainer>
                    <Typography type="caption" color="textGray">
                      {t(stockTableTranslations.index.key, stockTableTranslations.index.defaultValue)}
                    </Typography>
                    <Typography>{index}</Typography>
                  </ColumnFlexContainer>
                </RowFlexContainer>
                <RowFlexContainer
                  flex={1}
                  borderRadius={[2]}
                  gap={[3]}
                  backgroundColor={mode === 'dark' ? 'backgroundSelectedHover' : 'backgroundTableHeader'}
                  padding={[3]}
                >
                  <Icon icon={Icons.PRODUCTCATEGORY} size="small" />
                  <ColumnFlexContainer>
                    <Typography type="caption" color="textGray">
                      {t(stockTableTranslations.subindex.key, stockTableTranslations.subindex.defaultValue)}
                    </Typography>
                    <Typography>{subIndex}</Typography>
                  </ColumnFlexContainer>
                </RowFlexContainer>
              </RowFlexContainer>
            </ColumnFlexContainer>
          </ColumnFlexContainer>
          {footerContent && (
            <ColumnFlexContainer
              gap={[4]}
              padding={[5]}
              borderRadius={[0, 0, 4, 4]}
              backgroundColor={mode === 'dark' ? 'backgroundDark' : 'baseGray'}
            >
              {footerContent}
            </ColumnFlexContainer>
          )}
        </ColumnFlexContainer>
      )}
      {isBulkAction && (
        <StyledMultipleCardWrapper
          justifyContent="between"
          alignItems="center"
          padding={[2]}
          data-testid="multiple-stock-details-card"
        >
          <RowFlexContainer>
            <StyledImage
              data-testid="article-image"
              src={imageUrl}
              size="small"
              alt={productName}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = articlePlaceholder;
              }}
            />
            <ColumnFlexContainer margin={[0, 0, 0, 3]}>
              <Typography margin={[0, 0, 1]}>{sicScope}</Typography>
              <RowFlexContainer style={{ alignItems: 'center' }}>
                <StyledIcon margin={[0, 1, 0, 0]} icon={Icons.ALLWIDGETS} sizeSpacing={[3]} color="textGray" />
                <StyledTypography type="caption" color="textGray">
                  {departmentNumber}/{departmentName}
                </StyledTypography>
              </RowFlexContainer>
            </ColumnFlexContainer>
          </RowFlexContainer>
          {modalView && (
            <StyledActionIcon
              data-testid={`action-icon-${modalView}`}
              icon={getIcon()}
              size="small"
              modalView={modalView}
              onClick={() => {
                if (removeItem && modalView === ModalViews.INITIAL) {
                  removeItem();
                }
              }}
            />
          )}
        </StyledMultipleCardWrapper>
      )}
    </>
  );
};
